import { formatCpfOrCnpj, formatOnlyAlphanumeric, formatToBRL } from '@interco/lib-util'

interface IStep {
  key: string
  value?: string | number
  copy?: boolean
  copyValue?: string
}

const formatCnpj = (value?: string) => {
  if (!value) {
    return value
  }

  const sanitized = formatOnlyAlphanumeric(value.toUpperCase())
  const masked = formatCpfOrCnpj({ value: sanitized })

  return masked || value
}

const sanitizeCnpj = (value?: string) => {
  if (!value) {
    return value
  }

  return formatOnlyAlphanumeric(value.toUpperCase())
}

const cnpjStep = (key: string, value?: string): IStep => ({
  key,
  value: formatCnpj(value),
  copy: true,
  copyValue: sanitizeCnpj(value),
})

const formatGlobalCurrency = (value: number) => {
  if (value === undefined) {
    return undefined
  }

  return formatToBRL(value)
}
export const step = (
  productKey: ProductKey,
  serviceKey: ServiceKey,
  service: Service,
  isMeiOrPj: boolean,
  choosedYear: number,
): Array<IStep> => {
  if (isMeiOrPj) {
    return []
  }
  if (productKey === 'assetsRights') {
    switch (serviceKey) {
      case 'checkingAccount':
        return [
          { key: 'Localização', value: service.country },
          cnpjStep('CNPJ da fonte pagadora', service.payingSourceFtin),
          { key: 'Discriminação', value: service.description },
          { key: 'Código/Banco', value: service.bankCode },
          { key: 'Agência', value: service.agency },
          { key: 'Conta/Dígito', value: service.account },
          {
            key: `Situação em dez/${choosedYear - 1}`,
            value: formatGlobalCurrency(service?.balanceLastYear as number),
          },
          {
            key: `Situação em dez/${choosedYear}`,
            value: formatGlobalCurrency(service?.balanceYear as number),
          },
        ]
      case 'savingsAccount':
        return [
          { key: 'Localização', value: service.country },
          cnpjStep('CNPJ da fonte pagadora', service.payingSourceFtin),
          { key: 'Discriminação', value: service.description },
          { key: 'Código/Banco', value: service.bankCode },
          { key: 'Agência', value: service.agency },
          { key: 'Conta/Dígito', value: service.account },
          {
            key: `Situação em dez/${choosedYear - 1}`,
            value: formatGlobalCurrency(service?.balanceLastYear as number),
          },
          {
            key: `Situação em dez/${choosedYear}`,
            value: formatGlobalCurrency(service?.balanceYear as number),
          },
        ]
      case 'fixedIncome':
        return [
          { key: 'Localização', value: service.country },
          cnpjStep('CNPJ da fonte pagadora', service.payingSourceFtin),
          { key: 'Discriminação', value: service.description },
          {
            key: `Situação em dez/${choosedYear - 1}`,
            value: formatGlobalCurrency(service?.balanceLastYear as number),
          },
          {
            key: `Situação em dez/${choosedYear}`,
            value: formatGlobalCurrency(service?.balanceYear as number),
          },
          { key: 'Nome da fonte pagadora', value: service.payingSourceName },
          { key: 'Valor', value: formatGlobalCurrency(service?.value as number) },
        ]
      case 'treasuryDirect':
        return [
          { key: 'Localização', value: service.country },
          cnpjStep('CNPJ da fonte pagadora', service.payingSourceFtin),
          { key: 'Discriminação', value: service.description },
          {
            key: `Situação em dez/${choosedYear - 1}`,
            value: formatGlobalCurrency(service?.balanceLastYear as number),
          },
          {
            key: `Situação em dez/${choosedYear}`,
            value: formatGlobalCurrency(service?.balanceYear as number),
          },
        ]
      case 'investmentFund':
        return [
          { key: 'Localização', value: service.country },
          cnpjStep('CNPJ', service.payingSourceFtin),
          { key: 'Discriminação', value: service.description },
          {
            key: `Situação em dez/${choosedYear - 1}`,
            value: formatGlobalCurrency(service?.balanceLastYear as number),
          },
          {
            key: `Situação em dez/${choosedYear}`,
            value: formatGlobalCurrency(service?.balanceYear as number),
          },
        ]
      case 'infrastructureFund':
        return [
          { key: 'Localização', value: service.country },
          cnpjStep('CNPJ', service.payingSourceFtin),
          { key: 'Discriminação', value: service.description },
          {
            key: `Quantidade em dez/${choosedYear - 1}`,
            value: service?.balanceLastYear,
          },
          {
            key: `Quantidade em dez/${choosedYear}`,
            value: service?.balanceYear,
          },
        ]
      case 'realEstateFund':
        return [
          { key: 'Localização', value: service.country },
          cnpjStep('CNPJ da fonte pagadora', service.payingSourceFtin),
          { key: 'Discriminação', value: service.description },
          {
            key: `Quantidade em dez/${choosedYear - 1}`,
            value: service?.balanceLastYear,
          },
          {
            key: `Quantidade em dez/${choosedYear}`,
            value: service?.balanceYear,
          },
        ]

      case 'consortium':
        return [
          { key: 'Localização', value: service.country },
          cnpjStep('CNPJ da fonte pagadora', service.payingSourceFtin),
          { key: 'Discriminação', value: service.description },
          {
            key: `Situação em dez/${choosedYear - 1}`,
            value: formatGlobalCurrency(service?.balanceLastYear as number),
          },
          {
            key: `Situação em dez/${choosedYear}`,
            value: formatGlobalCurrency(service?.balanceYear as number),
          },
          { key: 'Nome da fonte pagadora', value: service.payingSourceName },
          { key: 'Valor', value: formatGlobalCurrency(service?.value as number) },
        ]

      case 'variableIncomeAssets':
        // return [
        //   { key: 'Localização', value: service.country },
        //   { key: 'Ativo', value: service.assetName },
        //   { key: 'CNPJ', value: service.payingSourceFtin, copy: true },
        //   { key: 'Discriminação', value: service.description, copy: true },
        //   { key: `Quantidade em dez/${choosedYear-1}`, value: service.lastYearPosition },
        //   {
        //     key: `Quantidade em dez/${choosedYear}`,
        //     value: service.currentYearPosition,
        //   },
        //   { key: `Tipo`, value: service.saleType },
        // ]
        return []
      default:
        return []
    }
  }

  if (productKey === 'realDebtOnus') {
    switch (serviceKey) {
      case 'payrollLoan':
        return [
          { key: 'Localização', value: service.country },
          { key: 'Discriminação', value: service.description },
          {
            key: `Situação em dez/${choosedYear - 1}`,
            value: formatGlobalCurrency(service.debitBalanceLastYear as number),
          },
          {
            key: `Situação em dez/${choosedYear}`,
            value: formatGlobalCurrency(service.debitBalanceYear as number),
          },
          {
            key: `Valor pago em ${choosedYear}`,
            value: formatGlobalCurrency(service.paidAmountCurrentYear as number),
          },
        ]
      case 'personalLoan':
        return [
          { key: 'Localização', value: service.country },
          { key: 'Discriminação', value: service.description },
          {
            key: `Situação em dez/${choosedYear - 1}`,
            value: formatGlobalCurrency(service.debitBalanceLastYear as number),
          },
          {
            key: `Situação em dez/${choosedYear}`,
            value: formatGlobalCurrency(service.debitBalanceYear as number),
          },
          {
            key: `Valor pago em ${choosedYear}`,
            value: formatGlobalCurrency(service.paidAmountCurrentYear as number),
          },
        ]
      case 'realEstateLoan':
        return [
          { key: 'Localização', value: service.country },
          { key: 'Discriminação', value: service.description },
          {
            key: `Situação em dez/${choosedYear - 1}`,
            value: formatGlobalCurrency(service.balanceLastYear as number),
          },
          {
            key: `Situação em dez/${choosedYear}`,
            value: formatGlobalCurrency(service.balanceYear as number),
          },
          {
            key: `Valor pago em ${choosedYear}`,
            value: formatGlobalCurrency(service.paidAmountCurrentYear as number),
          },
        ]
      default:
        return []
    }
  }

  if (productKey === 'debtsLinkedRuralActivities') {
    switch (serviceKey) {
      case 'ruralFinance':
        return [
          { key: 'Número do contrato', value: service.contractNumber },
          cnpjStep('CNPJ da fonte pagadora', service.payingSourceFtin),
          { key: 'Nome da fonte pagadora', value: service.payingSourceName },
          { key: 'Discriminação', value: service.description },
          {
            key: `Situação em dez/${choosedYear - 1}`,
            value: formatGlobalCurrency(service.debitBalanceLastYear as number),
          },
          {
            key: `Situação em dez/${choosedYear}`,
            value: formatGlobalCurrency(service.debitBalanceYear as number),
          },
        ]
      default:
        return []
    }
  }

  if (productKey === 'incomeTaxFree') {
    switch (serviceKey) {
      case 'savings':
        return [
          cnpjStep('CNPJ da fonte pagadora', service.payingSourceFtin),
          { key: 'Nome da fonte pagadora', value: service.payingSourceName },
          { key: 'Valor', value: formatGlobalCurrency(service?.value as number) },
        ]
      case 'fixedIncome':
        return [
          cnpjStep('CNPJ da fonte pagadora', service.payingSourceFtin),
          { key: 'Nome da fonte pagadora', value: service.payingSourceName },
          { key: 'Valor', value: formatGlobalCurrency(service?.value as number) },
        ]
      case 'infrastructureFund':
        return [
          cnpjStep('CNPJ da fonte pagadora', '18.945.670/0001-46'),
          { key: 'Nome da fonte pagadora', value: 'Inter Distrib de Tit e Val Mob LTDA' },
          cnpjStep('CNPJ do fundo', service.payingSourceFtin),
          { key: 'Nome do fundo', value: service.payingSourceName },
          { key: 'Valor', value: formatGlobalCurrency(service?.value as number) },
        ]
      case 'realEstateFund':
        return [
          cnpjStep('CNPJ da fonte pagadora', '18.945.670/0001-46'),
          { key: 'Nome da fonte pagadora', value: 'Inter Distrib de Tit e Val Mob LTDA' },
          cnpjStep('CNPJ do fundo imobiliário', service.payingSourceFtin),
          { key: 'Nome do fundo imobiliário', value: service.payingSourceName },
          { key: 'Valor', value: formatGlobalCurrency(service?.value as number) },
        ]
      default:
        return []
    }
  }

  if (productKey === 'incomeSubjectExclusiveTaxation') {
    switch (serviceKey) {
      case 'fixedIncome':
        return [
          cnpjStep('CNPJ da fonte pagadora', service.payingCompanyFtin),
          { key: 'Nome da fonte pagadora', value: service.payingCompanyName },
          { key: 'Discriminação', value: service.description },
          { key: 'Valor', value: formatGlobalCurrency(service?.value as number) },
        ]
      case 'treasuryDirect':
        return [
          cnpjStep('CNPJ da fonte pagadora', service.payingCompanyFtin),
          { key: 'Nome da fonte pagadora', value: service.payingCompanyName },
          { key: 'Discriminação', value: service.description },
          { key: 'Valor', value: formatGlobalCurrency(service?.value as number) },
        ]
      case 'investmentFund':
        return [
          cnpjStep('CNPJ da fonte pagadora', '18.945.670/0001-46'),
          { key: 'Nome da fonte pagadora', value: 'Inter Distrib de Tit e Val Mob LTDA' },
          cnpjStep('CNPJ do fundo de investimento', service.payingCompanyFtin),
          { key: 'Nome do fundo de investimento', value: service.payingCompanyName },
          { key: 'Valor', value: formatGlobalCurrency(service?.value as number) },
        ]
      case 'realEstateFund':
        return [
          cnpjStep('CNPJ da fonte pagadora', '18.945.670/0001-46'),
          { key: 'Nome da fonte pagadora', value: 'Inter Distrib de Tit e Val Mob LTDA' },
          cnpjStep('CNPJ do fundo imobiliário', service.payingCompanyFtin),
          { key: 'Nome do fundo imobiliário', value: service.payingCompanyName },
          { key: 'Discriminação', value: service.description },
          { key: 'Valor', value: formatGlobalCurrency(service?.value as number) },
        ]
      default:
        return []
    }
  }

  return []
}
